import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useAuth } from '../../context/AuthContext';
import { isLicenseRequired } from '../../lib/licenseStatus';
import { fetchOnboardingPreference, markOnboardingSeen, updateOnboardingPreference } from '../../services/onboardingService';
import { currentTourVersion, tourSlides, type OnboardingSlide } from './slides';

/**
 * Onboarding tour state for the whole signed-in app.
 *
 *  - `requestAutoStart()` is called by every AppLayout mount (OnboardingAutoStart).
 *    The first call of an app session (sessionStorage flag, cleared on logout by
 *    lib/userSession) loads the user's `showOnStart` preference and opens the tour
 *    when it is true. Later calls — route changes — do nothing.
 *  - Closing, skipping, finishing or "Try it now" never change the preference;
 *    only `setShowOnStart` does (the checkbox in the tour and in Settings).
 *  - Tour versions (slides.ts): when the newest switched-on slide is newer than
 *    the version the user saw (`seenVersion`), the tour opens once regardless of
 *    showOnStart and that version is reported as seen. A user who saw an older
 *    version gets "What's new" (only the slides added since); a new account
 *    (seenVersion 0) gets the full tour. Opening the tour by hand (`open()`,
 *    Help, Settings) always shows all of it.
 *  - Never auto-opens while the licence notice is shown or in an automated browser
 *    (navigator.webdriver: end-to-end suites log in as fresh users and must not be
 *    blocked by a modal). Opening it by hand (`open()`) always works.
 */
export type OnboardingLoadState = 'idle' | 'loading' | 'loaded' | 'error';

export interface OnboardingContextValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  /** The stored preference; null until loaded. */
  showOnStart: boolean | null;
  loadState: OnboardingLoadState;
  loadPreference: () => void;
  /** Optimistic update; reverts and sets `saveFailed` when the request fails. */
  setShowOnStart: (value: boolean) => Promise<void>;
  saveFailed: boolean;
  saving: boolean;
  requestAutoStart: () => void;
  /**
   * The automatic start of this session has been decided (tour opened or not).
   * Other automatic dialogs (the feedback prompt) wait for it, so two modals
   * never open at once.
   */
  autoStartSettled: boolean;
  /** The slides of the current showing (full tour or "What's new"). */
  slides: readonly OnboardingSlide[];
  /** This showing is "What's new" (only the slides added since the user's last tour). */
  whatsNew: boolean;
}

export const OnboardingContext = createContext<OnboardingContextValue | null>(null);

/** Per-session guard: the tour opens automatically at most once per app session. */
export const ONBOARDING_SESSION_KEY = 'reefguide-onboarding-auto-start';

function readSessionFlag(): boolean {
  try {
    return sessionStorage.getItem(ONBOARDING_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function writeSessionFlag(): void {
  try {
    sessionStorage.setItem(ONBOARDING_SESSION_KEY, '1');
  } catch {
    // storage unavailable — the in-memory guard still prevents a second open
  }
}

function isAutomatedBrowser(): boolean {
  try {
    return typeof navigator !== 'undefined' && navigator.webdriver === true;
  } catch {
    return false;
  }
}

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const isAuthenticated = auth.state?.isAuthenticated === true;
  const [isOpen, setIsOpen] = useState(false);
  const [showOnStart, setShowOnStartState] = useState<boolean | null>(null);
  const [loadState, setLoadState] = useState<OnboardingLoadState>('idle');
  const [saveFailed, setSaveFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [autoStartSettled, setAutoStartSettled] = useState(false);
  const [voice, setVoice] = useState(false);
  const [whatsNewSince, setWhatsNewSince] = useState<number | null>(null);
  const attemptedRef = useRef(false);
  const requestSeq = useRef(0);

  // A sign-out ends the session: close the tour and forget the loaded preference,
  // so the next sign-in (same tab) starts a new session.
  useEffect(() => {
    if (isAuthenticated) return;
    attemptedRef.current = false;
    requestSeq.current += 1;
    setIsOpen(false);
    setShowOnStartState(null);
    setLoadState('idle');
    setSaveFailed(false);
    setAutoStartSettled(false);
    setVoice(false);
    setWhatsNewSince(null);
  }, [isAuthenticated]);

  const load = useCallback(async (): Promise<{ showOnStart: boolean; seenVersion: number; voice: boolean } | null> => {
    const seq = ++requestSeq.current;
    setLoadState('loading');
    try {
      const pref = await fetchOnboardingPreference();
      if (seq !== requestSeq.current) return null;
      setShowOnStartState(pref.showOnStart);
      setVoice(pref.voice === true);
      setLoadState('loaded');
      return { showOnStart: pref.showOnStart, seenVersion: pref.seenVersion ?? 0, voice: pref.voice === true };
    } catch {
      if (seq === requestSeq.current) setLoadState('error');
      return null;
    }
  }, []);

  const loadPreference = useCallback(() => {
    void load();
  }, [load]);

  const requestAutoStart = useCallback(() => {
    if (attemptedRef.current || !isAuthenticated) return;
    if (readSessionFlag()) {
      attemptedRef.current = true;
      setAutoStartSettled(true);
      return;
    }
    if (isAutomatedBrowser() || isLicenseRequired()) {
      setAutoStartSettled(true);
      return;
    }
    attemptedRef.current = true;
    writeSessionFlag();
    void load().then((value) => {
      setAutoStartSettled(true);
      if (!value) return;
      // Slides newer than the user's tour open once for everyone, then count as seen.
      // Users who saw an older tour get only what is new; new accounts the full tour.
      const current = currentTourVersion({ voice: value.voice });
      const newVersion = value.seenVersion < current;
      setWhatsNewSince(newVersion && value.seenVersion >= 1 ? value.seenVersion : null);
      if (value.showOnStart || newVersion) setIsOpen(true);
      if (newVersion) void markOnboardingSeen(current).catch(() => undefined);
    });
  }, [isAuthenticated, load]);

  const setShowOnStart = useCallback(async (value: boolean): Promise<void> => {
    const previous = showOnStart;
    const seq = ++requestSeq.current;
    setShowOnStartState(value);
    setLoadState('loaded');
    setSaveFailed(false);
    setSaving(true);
    try {
      const saved = await updateOnboardingPreference(value);
      if (seq === requestSeq.current) setShowOnStartState(saved.showOnStart);
    } catch {
      if (seq === requestSeq.current) {
        setShowOnStartState(previous);
        setSaveFailed(true);
      }
    } finally {
      if (seq === requestSeq.current) setSaving(false);
    }
  }, [showOnStart]);

  const open = useCallback(() => {
    setSaveFailed(false);
    setWhatsNewSince(null);
    setIsOpen(true);
    if (showOnStart === null && loadState !== 'loading') void load();
  }, [showOnStart, loadState, load]);

  const close = useCallback(() => setIsOpen(false), []);

  const slides = useMemo(() => tourSlides({ voice }, whatsNewSince), [voice, whatsNewSince]);
  const whatsNew = whatsNewSince !== null && slides.length < tourSlides({ voice }).length;

  const value = useMemo<OnboardingContextValue>(
    () => ({
      isOpen,
      open,
      close,
      showOnStart,
      loadState,
      loadPreference,
      setShowOnStart,
      saveFailed,
      saving,
      requestAutoStart,
      autoStartSettled,
      slides,
      whatsNew,
    }),
    [isOpen, open, close, showOnStart, loadState, loadPreference, setShowOnStart, saveFailed, saving, requestAutoStart, autoStartSettled, slides, whatsNew],
  );

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

/** The onboarding state, or null outside an OnboardingProvider (e.g. isolated page tests). */
export function useOnboardingOptional(): OnboardingContextValue | null {
  return useContext(OnboardingContext);
}

export function useOnboarding(): OnboardingContextValue {
  const ctx = useContext(OnboardingContext);
  if (ctx === null) throw new Error('useOnboarding must be used within an OnboardingProvider');
  return ctx;
}
